import { Component, ElementRef, ViewChild, afterNextRender, OnDestroy, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import * as THREE from 'three';
import { isAuth } from './auth';

@Component({
  selector: 'app-www',
  standalone: true,
  template: `
    <div class="landing-container">
      <canvas #canvasRef></canvas>
      
      <header class="hero-content">
        <span class="badge">Next-Gen WebXR & 3D</span>
        <h1>Immersive Digital ESFA</h1>
   
        <p class="badge">
      Bienvenido: {{ auth?.name }}
    </p>
 
        <p>Build blazing-fast, interactive 3D experiences seamlessly integrated with modern ESFA.</p>
        <div class="cta-group">
          <button class="primary-btn" (click)="navigateToDocumentoswww()">Empezar</button>
          <button class="secondary-btn" (click)="navigateToDocumentos()">Documentos</button>
        </div>
      </header>
    </div>
  `,
  styles: [`
    .landing-container {
      position: relative;
      width: 100vw;
      height: 100vh;
      overflow: hidden;
      background: #020617;
      font-family: system-ui, -apple-system, sans-serif;
    }
    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: block;
      z-index: 1;
    }
    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
      text-align: center;
      max-width: 700px;
      padding: 0 20px;
      pointer-events: none;
    }
    .badge {
      display: inline-block;
      padding: 6px 16px;
      background: rgba(56, 189, 248, 0.1);
      border: 1px solid rgba(56, 189, 248, 0.3);
      color: #38bdf8;
      border-radius: 9999px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-bottom: 20px;
      backdrop-filter: blur(4px);
    }
    h1 {
      color: #f8fafc;
      font-size: clamp(2.5rem, 5vw, 4.5rem);
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 20px;
      letter-spacing: -0.02em;
    }
    p {
      color: #94a3b8;
      font-size: clamp(1rem, 2vw, 1.25rem);
      line-height: 1.6;
      margin-bottom: 35px;
    }
    .cta-group {
      display: flex;
      gap: 16px;
      justify-content: center;
      pointer-events: auto;
    }
    button {
      padding: 12px 28px;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .primary-btn {
      background: #38bdf8;
      color: #020617;
      border: none;
      box-shadow: 0 4px 14px rgba(56, 189, 248, 0.4);
    }
    .primary-btn:hover {
      background: #0ea5e9;
      transform: translateY(-2px);
    }
    .secondary-btn {
      background: transparent;
      color: #f8fafc;
      border: 1px solid rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(4px);
    }
    .secondary-btn:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  `]
})
export class Wwww implements OnDestroy {
  @ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  auth: any = isAuth();

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private animationFrameId = 0;
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private torusKnot!: THREE.Mesh;
  private mouseX = 0;
  private mouseY = 0;
  private targetX = 0;
  private targetY = 0;

  // Bound handler references for clean event removal
  private boundOnResize = this.onWindowResize.bind(this);
  private boundOnMouseMove = this.onMouseMove.bind(this);

  constructor() {
    afterNextRender(() => {
      this.initScene();
    });
  }

  navigateToDocumentos() {
    this.router.navigate(['/documentos']);
  }
  navigateToDocumentoswww() {
    this.router.navigate(['/login']);
  }

  private initScene() {
    const canvas = this.canvasRef.nativeElement;
    const width = window.innerWidth;
    const height = window.innerHeight;

    // 1. Scene & Camera
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.z = 5;

    // 2. Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // 3. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x38bdf8, 3);
    pointLight.position.set(5, 5, 5);
    this.scene.add(pointLight);

    const pointLight2 = new THREE.PointLight(0x818cf8, 3);
    pointLight2.position.set(-5, -5, 5);
    this.scene.add(pointLight2);

    // 4. Hero 3D Object (Glowing Torus Knot)
    const geometry = new THREE.TorusKnotGeometry(1.2, 0.35, 128, 32);
    const material = new THREE.MeshStandardMaterial({
      color: 0x0f172a,
      roughness: 0.2,
      metalness: 0.9,
      wireframe: false
    });
    this.torusKnot = new THREE.Mesh(geometry, material);
    this.scene.add(this.torusKnot);

    // 5. Event Listeners using cached bound references
    window.addEventListener('resize', this.boundOnResize);
    window.addEventListener('mousemove', this.boundOnMouseMove);

    // 6. Animation Loop
    this.ngZone.runOutsideAngular(() => {
      const animate = () => {
        this.animationFrameId = requestAnimationFrame(animate);

        // Smooth mouse parallax interpolation
        this.targetX += (this.mouseX - this.targetX) * 0.05;
        this.targetY += (this.mouseY - this.targetY) * 0.05;

        this.torusKnot.rotation.x = this.targetY * 0.5 + Date.now() * 0.0005;
        this.torusKnot.rotation.y = this.targetX * 0.5 + Date.now() * 0.0008;

        this.renderer.render(this.scene, this.camera);
      };
      animate();
    });
  }

  private onMouseMove(event: MouseEvent) {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  private onWindowResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.animationFrameId);
    
    // Properly clean up listeners using the same cached references
    window.removeEventListener('resize', this.boundOnResize);
    window.removeEventListener('mousemove', this.boundOnMouseMove);

    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}